import { useEffect, useState } from "react";
import { supabase } from "@core/lib/supabase";
import { logger } from "@core/lib/logger";
import { captureInviteToken, markInviteOnly } from "@core/lib/inviteToken";
import type { InviteGateReason } from "@core/components/voting/InviteGate";

/**
 * Préflight d'accès à un sondage « sur invitation », commun à tous les produits.
 *
 * Extrait de `apps/date-polls/src/components/Voting.tsx`, où il ne servait qu'aux
 * sondages de dates : le serveur applique pourtant `voteAccess='invite'` dans
 * LES DEUX RPC d'écriture depuis `20260816092000`, et `check_poll_invitation`
 * ne regarde que `settings.voteAccess` — jamais `polls.type`. Sans ce hook côté
 * questionnaires, un créateur pouvait cocher « Sur invitation » et obtenir un
 * formulaire auquel personne ne pouvait répondre : le serveur exigeait une
 * preuve que le client ne savait ni recevoir ni transmettre.
 *
 * Rien n'est décidé ici. Le verdict vient de la RPC, et c'est de toute façon
 * `submit_poll_response` / `upsert_poll_response` qui tranchent à l'écriture :
 * cet écran évite de faire remplir un formulaire pour le refuser à l'envoi.
 */

export interface InviteAccess {
  /** Le sondage annonce-t-il `voteAccess='invite'` ? */
  isInviteOnly: boolean;
  /** Motif de refus renvoyé par le serveur, `null` si l'accès est ouvert. */
  inviteReason: InviteGateReason | null;
  /** « m****e@exemple.fr » quand l'invitation est reconnue. */
  inviteEmailMasked: string | null;
  /** Vrai tant que le préflight n'a pas répondu (ne rien afficher entre-temps). */
  isCheckingAccess: boolean;
  /** Jeton mémorisé pour ce sondage, à passer aux appels d'écriture. */
  inviteToken: string | null;
}

export function useInviteAccess(
  pollId?: string | null,
  settings?: { voteAccess?: unknown } | null,
): InviteAccess {
  const isInviteOnly = settings?.voteAccess === "invite";

  const [inviteReason, setInviteReason] = useState<InviteGateReason | null>(
    null,
  );
  const [inviteEmailMasked, setInviteEmailMasked] = useState<string | null>(
    null,
  );
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [inviteToken, setInviteToken] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!pollId) return;

    markInviteOnly(pollId, isInviteOnly);

    // Capte `?invite=` et le retire de l'URL (sinon le jeton part dans le
    // Referer, les analytics et l'historique) — sur TOUT sondage. Sur un
    // sondage ouvert il ne conditionne pas l'accès, mais useVoting le transmet
    // au vote pour le rattacher à l'invitation (suivi « a voté », base des
    // relances ; cf. migration 20260915213000).
    const token = captureInviteToken(pollId);
    setInviteToken(token);

    if (!isInviteOnly) {
      setInviteReason(null);
      setInviteEmailMasked(null);
      setIsCheckingAccess(false);
      return;
    }

    (async () => {
      const { data, error } = await supabase
        .rpc("check_poll_invitation", {
          p_poll_id: pollId,
          p_token: token,
        })
        .maybeSingle();
      if (cancelled) return;

      if (error) {
        logger.warn("check_poll_invitation a échoué", "vote", {
          pollId,
          error: error.message,
        });
        // On n'ouvre pas l'accès par défaut : le serveur refusera de toute
        // façon le vote, autant l'annoncer tout de suite.
        setInviteReason("invite_required");
        setIsCheckingAccess(false);
        return;
      }

      const row = data as {
        valid: boolean;
        reason: string;
        email_masked: string | null;
      } | null;

      if (row?.valid) {
        setInviteReason(null);
        setInviteEmailMasked(row.email_masked ?? null);
      } else {
        setInviteReason((row?.reason as InviteGateReason) || "invite_required");
      }
      setIsCheckingAccess(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [isInviteOnly, pollId]);

  return {
    isInviteOnly,
    inviteReason,
    inviteEmailMasked,
    isCheckingAccess,
    inviteToken,
  };
}

export default useInviteAccess;
